import React, { useState } from "react";
import { cn } from "@/utils/utils";
import IconContainer from "@/components/ui/icon-container";
import {
  AlertBubble,
  AlertBubbleWarning,
  InfoCircle,
  Resolve,
  Close
} from "@dreamhub/designsystem/icons";
import Text, { TextVariant } from "@/components/ui/text";
import { Button, ButtonVariant } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";

export type BannerVariant =
  | "general"
  | "warning"
  | "info"
  | "success"
  | "error";

export type BannerSize = "default" | "small";
export type { ButtonVariant } from "@/components/ui/button";

interface BannerProps {
  variant: BannerVariant;
  size?: BannerSize;
  title?: string;
  titleVariant?: TextVariant;
  buttonText?: string;
  buttonVariant?: ButtonVariant;
  onButtonClick?: () => void;
  showCloseButton?: boolean;
  onClose?: () => void;
  className?: string;
  children?: React.ReactNode;
}

const variantConfig = {
  general: {
    icon: AlertBubble,
    iconColor: "text-new",
    iconGlow: "drop-shadow-purple-glow",
    background: "bg-surface-banner-general",
    borderColor: "border-border-banner-general"
  },
  warning: {
    icon: AlertBubbleWarning,
    iconColor: "text-warning",
    iconGlow: "",
    background: "bg-badges-warning",
    borderColor: "border-badges-warning"
  },
  info: {
    icon: InfoCircle,
    iconColor: "text-natural",
    iconGlow: "",
    background: "bg-badges-natural",
    borderColor: "border-badges-natural"
  },
  success: {
    icon: Resolve,
    iconColor: "text-positive",
    iconGlow: "",
    background: "bg-badges-positive",
    borderColor: "border-badges-positive"
  },
  error: {
    icon: AlertBubbleWarning,
    iconColor: "text-negative",
    iconGlow: "",
    background: "bg-badges-negative",
    borderColor: "border-badges-negative"
  }
};

const Banner: React.FC<BannerProps> = ({
  variant,
  size = "default",
  title,
  titleVariant = "lgBody",
  buttonText,
  buttonVariant = "primary",
  onButtonClick,
  showCloseButton = true,
  onClose,
  className,
  children
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const config = variantConfig[variant];
  const IconComponent = config.icon;

  const handleClose = () => {
    setIsVisible(false);
    onClose?.();
  };

  if (!isVisible) {
    return null;
  }

  if (children) {
    return (
      <div
        className={cn(
          "w-full rounded-lg border-[1.5px]",
          config.background,
          config.borderColor,
          className
        )}
        data-test={`banner-${variant}`}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center w-full justify-between rounded-lg shadow-tooltip border-[1.5px]",
        size === "small" ? "h-fit p-2 " : "h-fit p-4",
        config.background,
        config.borderColor,
        className
      )}
      data-test={`banner-${variant}`}
    >
      <div className="flex items-center gap-4">
        <IconContainer
          size="md"
          className={cn(config.iconColor, config.iconGlow)}
        >
          <IconComponent />
        </IconContainer>
        <Text variant={titleVariant}>{title}</Text>
      </div>

      <div className="flex items-center gap-4">
        {buttonText && onButtonClick && (
          <Button
            variant={buttonVariant}
            size="lg"
            className={size === "small" ? "h-7" : "h-8"}
            onClick={onButtonClick}
            data-test={`banner-${variant}-action-button`}
          >
            {buttonText}
          </Button>
        )}

        {showCloseButton && (
          <IconButton
            variant="iconNoBg"
            size="md"
            onClick={handleClose}
            data-test={`banner-${variant}-close-button`}
          >
            <Close />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default Banner;
